// src/components/admin/StudentUpload.tsx
import React, { useState } from 'react';
import { loadStudentMapFromCSV, isStudentMapLoaded } from '../../utils/studentMapUtils';

const StudentUpload: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };
  
  const handleUpload = async () => {
    if (!file) {
      setMessage('Please select a file first');
      setMessageType('error');
      return;
    }
    
    try {
      setLoading(true);
      await loadStudentMapFromCSV(file);
      setMessage('Student mapping loaded successfully! Names will now appear on forms.');
      setMessageType('success');
      setFile(null);
      
      // Clear file input
      const fileInput = document.getElementById('student-csv-upload') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setMessage(`Error loading file: ${(error as Error).message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Upload Student Names</h2>
      
      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-2">
          Upload a CSV file that maps user IDs to student names to display names on forms. 
          File must have columns: "Student Name", "Email Address", "Password", and "UID".
        </p>
        
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> This mapping is stored in your browser and doesn't modify the database.
            If you clear your browser cache or switch browsers, you'll need to upload the CSV again.
          </p>
        </div>
        
        <div className="flex items-center mt-4">
          <input
            id="student-csv-upload"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
          />
          <button
            onClick={handleUpload}
            disabled={!file || loading}
            className={`ml-4 px-4 py-2 rounded-md text-white ${
              !file || loading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {loading ? 'Loading...' : 'Upload'}
          </button>
        </div>
      </div>
      
      {message && (
        <div className={`p-3 rounded-md ${
          messageType === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {message}
        </div>
      )}
      
      <div className="mt-4">
        <p className="text-sm text-gray-600">
          Status: {isStudentMapLoaded() 
            ? <span className="text-green-600 font-medium">Student mapping loaded</span> 
            : <span className="text-yellow-600 font-medium">No student mapping loaded</span>}
        </p>
      </div>
    </div>
  );
};

export default StudentUpload;